import { useCallback, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { userState } from "../../../../recoil/auth";
import { isString } from "lodash";

const useQr = () => {
  const user = useRecoilValue(userState);

  const [isQrOpen, setIsQrOpen] = useState(false);

  const secretKey = new Uint8Array([
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
    22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32,
  ]);

  const [qrValue, setQrValue] = useState<string>(
    {
      userId: user?.id || 0,
      expire: new Date().getTime() + 30000, // 30초 후
    }.toString()
  );

  // 암호화 함수
  async function encryptData(data: any, secretKey: any) {
    // JSON 객체를 문자열로 변환
    const jsonString = JSON.stringify(data);

    // 텍스트를 UTF-8 인코딩
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(jsonString);

    // 비밀키를 CryptoKey로 가져오기
    const key = await window.crypto.subtle.importKey(
      "raw",
      secretKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );

    // 랜덤 초기화 벡터(IV) 생성
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    // 암호화 수행
    const encryptedContent = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: iv,
      },
      key,
      dataBuffer
    );

    // 암호화된 데이터와 IV를 결합
    const encryptedData = new Uint8Array(
      iv.length + encryptedContent.byteLength
    );
    encryptedData.set(iv);
    encryptedData.set(new Uint8Array(encryptedContent), iv.length);

    // Base64로 인코딩
    return btoa(String.fromCharCode.apply(null, Array.from(encryptedData)));
  }

  async function decryptData(encryptedBase64: string, secretKey: any) {
    // Base64 디코딩
    const encryptedData = new Uint8Array(
      atob(encryptedBase64)
        .split("")
        .map((char) => char.charCodeAt(0))
    );

    // IV 추출 (첫 12바이트)
    const iv = encryptedData.slice(0, 12);
    const data = encryptedData.slice(12);

    // 비밀키를 CryptoKey로 가져오기
    const key = await window.crypto.subtle.importKey(
      "raw",
      secretKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );

    // 복호화 수행
    const decryptedContent = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: iv,
      },
      key,
      data
    );

    // 복호화된 데이터를 문자열로 변환
    const decoder = new TextDecoder();
    const decryptedString = decoder.decode(decryptedContent);

    // JSON으로 파싱
    return JSON.parse(decryptedString);
  }

  // 사용 예시
  async function createQrValue() {
    // 32바이트(256비트) 비밀키 생성

    const originalData = {
      userId: user?.id || 0,
      expire: new Date().getTime() + 30000, // 30초 후
    };

    const encrypted = await encryptData(originalData, secretKey);
    setQrValue(encrypted);
  }

  useEffect(() => {
    if (user) {
      createQrValue();
    }
  }, [user, isQrOpen]);

  // 남은 시간 계산 (초 단위)
  const getRemainingSeconds = useCallback((expireTime: number) => {
    const now = new Date().getTime();
    const remaining = expireTime - now;
    return Math.max(0, Math.floor(remaining / 1000));
  }, []);

  useEffect(() => {
    const updateExpiredText = async () => {
      try {
        const decrypted = await decryptData(qrValue, secretKey);
        const remaining = getRemainingSeconds(decrypted.expire);
        if (remaining <= 0) {
          createQrValue();
        }
      } catch (e) {
        console.error(e);
      }
    };

    // 즉시 실행
    if (user) {
      updateExpiredText();
      // 매초마다 업데이트
      const interval = setInterval(updateExpiredText, 1000);

      return () => clearInterval(interval);
    }
  }, [qrValue, getRemainingSeconds, user?.id]);

  // 만료 여부 확인
  const isExpired = (expireTime: number) => {
    return new Date().getTime() > expireTime;
  };

  return {
    isQrOpen,
    setIsQrOpen,
    qrValue,
    setQrValue,
    getRemainingSeconds,
    isExpired,
  };
};

export default useQr;
